import { useEffect, useState } from "react";
import { FileSearchOutlined } from "@ant-design/icons";
import { Modal, Typography, Button, Card, Tooltip } from "antd";
import PropTypes from "prop-types";

import { ChallengeDetailCard } from "./ChallengeDetailCard.jsx";
import { ChallengeResultRows } from "./ChallengeResultRows.jsx";
import { TextViewerPre } from "../../components/custom-tools/text-viewer-pre/TextViewerPre.jsx";
import "./ChallengeModal.css";
import { formatNumberWithCommas } from "../../helpers/GetStaticData.js";
import { useCustomToolStore } from "../../store/custom-tool-store.js";
const { Title, Text } = Typography;

function ChallengeModal({ challengeData, context, tokenUsage }) {
  const [isOpen, setIsOpen] = useState(false);
  const isDataAvailable = challengeData?.runs?.length > 0;
  const iconClass = isDataAvailable ? "" : "icon-disabled";
  const [selectedItem, setSelectedItem] = useState({
    index: 0,
    title: "",
    data: "",
  });
  const { singlePassExtractMode } = useCustomToolStore();
  const handleItemClick = (index, title, data) => {
    if (data && typeof data === "object") {
      data = JSON.stringify(data, null, 4);
    }
    setSelectedItem({
      index,
      title,
      data,
    });
  };

  useEffect(() => {
    if (!challengeData) {
      return;
    }
    handleItemClick(0, challengeData?.file_name, context);
  }, [challengeData]);

  const getFileSearchIcon = () => {
    return (
      <FileSearchOutlined
        onClick={() => isDataAvailable && setIsOpen(true)}
        className={`prompt-card-actions-head ${iconClass}`}
      />
    );
  };

  return (
    <>
      <Tooltip
        title={
          isDataAvailable ? "LLMChallenge Log" : "No LLMChallenge Log Available"
        }
        className={`challenge-log-tooltip`}
      >
        {singlePassExtractMode ? (
          <Button disabled={!isDataAvailable}>{getFileSearchIcon()}</Button>
        ) : (
          getFileSearchIcon()
        )}
      </Tooltip>

      <Modal
        title="LLMChallenge Log"
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        width="92vw"
        className="challenge-modal"
        footer={
          <Button type="primary" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        }
        centered
      >
        {isDataAvailable && (
          <div className="display-flex-space-between">
            <div className="challenge-modal-section left">
              <Card className="challenger-card">
                <div
                  className={`prompt-card-div prompt-card-bg-col1 prompt-card-rad cursor-pointer ${
                    selectedItem?.index === 0 ? "active" : ""
                  }`}
                  onClick={() =>
                    handleItemClick(0, challengeData?.file_name, context)
                  }
                >
                  <Title level={5} className="challenge-file-title">
                    {challengeData?.file_name}
                  </Title>
                  <Text ellipsis={true}>{context}</Text>
                </div>
                <ChallengeResultRows
                  challengeData={challengeData}
                  handleItemClick={handleItemClick}
                  selectedItem={selectedItem}
                />
                <ChallengeDetailCard
                  padding={0}
                  data={{
                    ...challengeData,
                    type: "result",
                    title: "Result",
                    status:
                      challengeData?.runs?.[challengeData?.runs?.length - 1]
                        ?.status,
                  }}
                  onClick={() =>
                    handleItemClick(
                      (challengeData?.runs?.length ?? 0) + 2,
                      "Result",
                      challengeData?.result
                    )
                  }
                  isActive={
                    selectedItem?.index ===
                    (challengeData?.runs?.length ?? 0) + 2
                  }
                />
                <div className="challenge-card-result challenge-card-div">
                  <strong>
                    Input Tokens:{" "}
                    {formatNumberWithCommas(tokenUsage?.prompt_tokens)} | Output
                    Tokens:{" "}
                    {formatNumberWithCommas(tokenUsage?.completion_tokens)} |
                    Total Tokens: {tokenUsage?.total_tokens} | Total cost ${" "}
                    {formatNumberWithCommas(
                      tokenUsage?.cost_in_dollars === 0
                        ? 0
                        : tokenUsage?.cost_in_dollars?.toFixed(5)
                    )}
                  </strong>
                </div>
              </Card>
            </div>
            <div className="challenge-modal-section">
              <Title level={5} className="challenge-file-title">
                {selectedItem?.title}
              </Title>
              <div className="flex-1 overflow-y-auto">
                <TextViewerPre
                  text={
                    selectedItem?.data && typeof selectedItem?.data === "object"
                      ? JSON.stringify(selectedItem?.data, null, 4)
                      : selectedItem?.data
                  }
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

ChallengeModal.propTypes = {
  challengeData: PropTypes.object.isRequired,
  context: PropTypes.string.isRequired,
  tokenUsage: PropTypes.object.isRequired,
};

export { ChallengeModal };
